import React from "react";
import {Alert, Box, CircularProgress, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";

type EmailPreviewFrameProps = {
  html: string | null;
  status: "loading" | "ready" | "error";
  sender: string;
  subject: string;
  title?: string;
  minHeight?: number;
  fill?: boolean;
  bordered?: boolean;
  emptyMessage?: string;
  headerAction?: React.ReactNode;
};

export default function EmailPreviewFrame({
  html, status, sender, subject, title, minHeight = 600, fill = false, bordered = true, emptyMessage, headerAction,
}: EmailPreviewFrameProps): JSX.Element {
  const {t} = useTranslation();

  return (
    <Box sx={{display: "flex", flexDirection: "column", ...(bordered ? {border: "1px solid", borderColor: "ornament.dark", borderRadius: 1, overflow: "hidden"} : {}), ...(fill ? {flex: 1, minHeight: 0} : {})}}>
      <Box sx={{p: 2, backgroundColor: "ornament.main", borderBottom: "1px solid", borderColor: "ornament.dark", ...(headerAction ? {display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1} : {})}}>
        <Box sx={{minWidth: 0}}>
          <Typography variant="body2" sx={{mb: 1}}>
            <Box component="span" sx={{fontWeight: "bold"}}>{t("email_templates.preview.sender")} :</Box>{" "}{sender}
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{fontWeight: "bold"}}>{t("email_templates.preview.subject")} :</Box>{" "}{subject}
          </Typography>
        </Box>
        {headerAction}
      </Box>

      {html !== null
        ? <iframe srcDoc={html} style={{...(fill ? {flex: 1, minHeight: 0} : {minHeight}), border: "none", width: "100%"}} sandbox="allow-same-origin" title={title ?? "preview"} />
        : status === "loading"
          ? <Box sx={{display: "flex", justifyContent: "center", py: 2}}><CircularProgress size={24} /></Box>
          : status === "error"
            ? <Box sx={{p: 2}}><Typography variant="body2" color="error">{t("global.default_error_message")}</Typography></Box>
            : <Box sx={{p: 2}}><Alert severity="info" icon={false}>{emptyMessage ?? t("email_templates.preview.without_preview")}</Alert></Box>
      }
    </Box>
  );
}
